import * as React from 'react';

import { ChangeReleaseInputField } from '@webapp/store/release/action-creators';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

// Types
import { ReleaseType } from '@common/types/release/release-type';

// Utils
import { normalizeTitle } from '@webapp/utils/normalizers';

// Store
import AppState from '@webapp/store/state';
import { UpdateField } from '@webapp/store/release/action-creators';

// Components
import Box from '@components/layout/box';
import InputField from '@components/form/input';
import HintArrow from '@components/basics/hint-arrow';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';

interface TitleCtrlProps {
  className?: string;
  title?: string;
  releaseType?: ReleaseType;
  subscriptionPlan: string;
  coverUrl?: string;
  updateTitle: (value: string) => UpdateField<'title'>;
  handleUpgrade: () => void;
  setPopOver?: React.Dispatch<React.SetStateAction<boolean>>;
  trackTitleChange: (value: string) => ChangeReleaseInputField<'title'>;
}

const mapStateToProps = (state: AppState) => ({
  title: state.release.release?.title,
  releaseType: state.release.release?.type,
  subscriptionPlan: state.subscriptionManager.summary?.artists
    .find((a) => a.id === state.artist.active?.id)
    ?.subscriptions.find((s) => s.type === 'distribution')?.plan,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateTitle: (value: string) =>
    dispatch(new UpdateField<'title'>('title', value, undefined, true)),
  trackTitleChange: (value: string) => {
    dispatch(new ChangeReleaseInputField<'title'>('title', value));
  },
});

export const TitleCtrl: React.FC<TitleCtrlProps> = ({
  title,
  subscriptionPlan,
  updateTitle,
  trackTitleChange,
  ...props
}) => {
  const [originalValue, setOriginalValue] = React.useState(title);
  const [popOver, setPopOver] = React.useState<boolean>(false);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!originalValue) {
      setOriginalValue(title);
    }
  }, [title, originalValue]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (!originalValue) {
        return;
      }
      trackTitleChange(originalValue);
    }, 800);

    return () => clearTimeout(timer);
  }, [originalValue, trackTitleChange]);

  React.useEffect(() => {
    if (!originalValue) {
      return;
    }
    if (originalValue?.toLocaleLowerCase() !== title?.toLocaleLowerCase()) {
      return;
    }

    if (
      subscriptionPlan?.toUpperCase() === 'BEGINNER' &&
      originalValue !== normalizeTitle(title || '')
    ) {
      setPopOver(true);
    }
  }, [originalValue, title, subscriptionPlan, updateTitle]);

  const handleChange = (value: string) => {
    setOriginalValue(value);
    if (
      subscriptionPlan?.toUpperCase() === 'BEGINNER' &&
      value !== normalizeTitle(value)
    ) {
      updateTitle(normalizeTitle(value));
    } else {
      updateTitle(value);
    }
  };

  const validate = () => {
    if (!originalValue) {
      return setError(true);
    }
    return setError(false);
  };

  return (
    <Box noBorderBottom className={props.className}>
      <Typography className="mb-20" type="big-title" customFont="medium">
        <Translate id="RELEASE.COVER_MUSIC.RELEASE_NAME.TITLE" />
      </Typography>
      <InputField
        id="title"
        className={`${popOver ? 'has-popover' : ''}`}
        label={<Translate id="RELEASE.COVER_MUSIC.RELEASE_NAME.INPUT" />}
        value={title || ''}
        type="text"
        leftDecoration={
          <MotionOpacity>
            {props.coverUrl ? (
              <SquareImg src={props.coverUrl} alt={title} />
            ) : (
              <SquarePlaceholder />
            )}
          </MotionOpacity>
        }
        onChange={(value: string) => handleChange(value)}
        onBlur={() => validate()}
        onFocus={() => setError(false)}
        error={error ? <Translate id="RELEASE.MISSING_TITLE" /> : undefined}
      />
      {popOver && (
        <HintArrow
          title={<Translate id="RELEASE.COVER_MUSIC.RELEASE_NAME.HINT" />}
          onClick={props.handleUpgrade}
        />
      )}
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(TitleCtrl)`
  .has-popover {
    border-radius: 4px 4px 0 0;
  }
`);

export const SquarePlaceholder = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.box};
`;

const SquareImg = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
  background-color: ${({ theme }) => theme.colors.box};
`;
